import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { loadNomenclature, projectTypeList } from '../../utils/nomenclature';

const CreateProjectModal = ({ isOpen, onClose, onProjectCreated }) => {
  const { instance, accounts } = useMsal();
  
  // Wizard step state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  
  // Form data state
  const [formData, setFormData] = useState({
    job_number: '',
    job_name: '',
    client_name: '',
    project_type: '',
    division_id: '',
    discipline_ids: [],
    user_assignments: []
  });
  const [projectTypes, setProjectTypes] = useState(['Lump Sum', 'T&M', 'Unit Price', 'Cost Plus']);
  
  // Data loading state
  const [divisions, setDivisions] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Fetch divisions, disciplines, and users when modal opens
  const fetchData = useCallback(async () => {
    if (!isOpen || !accounts || accounts.length === 0) return;
    
    setLoadingData(true);
    try {
      const [divisionsRes, disciplinesRes, usersRes, nom] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/divisions'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/disciplines'),
        authenticatedApiCall(instance, accounts, 'GET', '/project-assignments/assignable-users'),
        loadNomenclature((method, url, data) => authenticatedApiCall(instance, accounts, method, url, data)).catch(() => null)
      ]);
      
      setDivisions(divisionsRes.data.data || []);
      setDisciplines(disciplinesRes.data.data || []);
      setUsers(usersRes.data.users || []);
      if (nom) setProjectTypes(projectTypeList(nom));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load form data');
    } finally {
      setLoadingData(false);
    }
  }, [isOpen, instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDisciplineToggle = (disciplineId) => {
    setFormData(prev => {
      const isSelected = prev.discipline_ids.includes(disciplineId);
      return {
        ...prev,
        discipline_ids: isSelected
          ? prev.discipline_ids.filter(id => id !== disciplineId)
          : [...prev.discipline_ids, disciplineId]
      };
    });
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => {
      const existingAssignment = prev.user_assignments.find(a => a.userId === userId);
      if (existingAssignment) {
        return {
          ...prev,
          user_assignments: prev.user_assignments.filter(a => a.userId !== userId)
        };
      } else {
        return {
          ...prev,
          user_assignments: [...prev.user_assignments, { userId }]
        };
      }
    });
  };

  const isUserSelected = (userId) => {
    return formData.user_assignments.some(a => a.userId === userId);
  };

  const filteredUsers = users.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      return formData.job_number.trim() !== '' && formData.job_name.trim() !== '' && formData.project_type.trim() !== '';
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < totalSteps && canProceedToNextStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const submitData = {
        job_number: formData.job_number,
        job_name: formData.job_name,
        client_name: formData.client_name || null,
        project_type: formData.project_type || null,
        division_id: formData.division_id || null,
        discipline_ids: formData.discipline_ids,
        user_assignments: formData.user_assignments
      };

      const createResponse = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/projects',
        submitData
      );

      if (createResponse.data.success) {
        resetForm();
        onProjectCreated(createResponse.data.project);
        onClose();
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setError(error.response?.data?.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      job_number: '',
      job_name: '',
      client_name: '',
      project_type: '',
      division_id: '',
      discipline_ids: [],
      user_assignments: []
    });
    setCurrentStep(1);
    setError('');
    setUserSearchTerm('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: '#1a1a2e',
      borderRadius: '12px',
      padding: '0',
      width: '90%',
      maxWidth: '800px',
      maxHeight: '85vh',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #2d2d44',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#16162a'
    },
    title: {
      margin: 0,
      color: '#e0e0e0',
      fontSize: '1.25rem',
      fontWeight: '600'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#888',
      padding: '0.25rem',
      lineHeight: 1
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'center',
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #2d2d44',
      backgroundColor: '#16162a'
    },
    stepDot: (isActive, isCompleted) => ({
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: isActive ? '#4a9eff' : isCompleted ? '#28a745' : '#444',
      margin: '0 0.5rem',
      transition: 'all 0.3s ease'
    }),
    stepLine: (isCompleted) => ({
      width: '60px',
      height: '2px',
      backgroundColor: isCompleted ? '#28a745' : '#444',
      alignSelf: 'center',
      transition: 'all 0.3s ease'
    }),
    stepLabel: {
      color: '#888',
      fontSize: '0.85rem',
      marginTop: '0.5rem',
      textAlign: 'center'
    },
    content: {
      padding: '2rem',
      overflowY: 'auto',
      flex: 1
    },
    stepTitle: {
      color: '#e0e0e0',
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1.5rem'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: '500',
      color: '#b0b0b0',
      fontSize: '0.9rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      backgroundColor: '#242438',
      color: '#e0e0e0',
      transition: 'border-color 0.2s ease'
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      backgroundColor: '#242438',
      color: '#e0e0e0',
      cursor: 'pointer'
    },
    checkboxGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: '0.75rem'
    },
    checkboxItem: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '0.75rem 1rem',
      backgroundColor: isSelected ? '#1e3a5f' : '#242438',
      borderRadius: '6px',
      cursor: 'pointer',
      border: `1px solid ${isSelected ? '#4a9eff' : '#3d3d5c'}`,
      transition: 'all 0.2s ease'
    }),
    checkbox: {
      marginRight: '0.75rem',
      width: '18px',
      height: '18px',
      accentColor: '#4a9eff'
    },
    userList: {
      maxHeight: '350px',
      overflowY: 'auto',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      backgroundColor: '#1a1a2e'
    },
    userItem: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #2d2d44',
      backgroundColor: isSelected ? '#1e3a5f' : 'transparent',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      gap: '1rem'
    }),
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 0
    },
    userName: {
      color: '#e0e0e0',
      fontWeight: '500',
      marginRight: '0.75rem'
    },
    userEmail: {
      color: '#888',
      fontSize: '0.85rem'
    },
    searchInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      backgroundColor: '#242438',
      color: '#e0e0e0',
      marginBottom: '1rem'
    },
    error: {
      padding: '0.75rem 1rem',
      backgroundColor: 'rgba(220, 53, 69, 0.15)',
      color: '#f8d7da',
      borderRadius: '6px',
      marginBottom: '1rem',
      border: '1px solid rgba(220, 53, 69, 0.3)'
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #2d2d44',
      display: 'flex',
      justifyContent: 'space-between',
      backgroundColor: '#16162a'
    },
    button: (variant) => ({
      padding: '0.75rem 1.5rem',
      backgroundColor: variant === 'primary' ? '#4a9eff' : variant === 'success' ? '#28a745' : '#3d3d5c',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }),
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    selectedCount: {
      backgroundColor: '#4a9eff',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.8rem',
      marginLeft: '0.5rem'
    },
    helpText: {
      color: '#888',
      fontSize: '0.85rem',
      marginTop: '1rem',
      padding: '0.75rem',
      backgroundColor: '#242438',
      borderRadius: '6px',
      border: '1px solid #3d3d5c'
    }
  };

  const renderStepIndicator = () => (
    <div style={styles.stepIndicator}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={styles.stepDot(currentStep === 1, currentStep > 1)} />
          <div style={styles.stepLine(currentStep > 1)} />
          <div style={styles.stepDot(currentStep === 2, currentStep > 2)} />
          <div style={styles.stepLine(currentStep > 2)} />
          <div style={styles.stepDot(currentStep === 3, false)} />
        </div>
        <div style={{ display: 'flex', marginTop: '0.5rem' }}>
          <span style={{ ...styles.stepLabel, width: '80px' }}>Details</span>
          <span style={{ ...styles.stepLabel, width: '80px' }}>Division</span>
          <span style={{ ...styles.stepLabel, width: '80px' }}>Users</span>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <>
      <h4 style={styles.stepTitle}>Project Details</h4>
      <div style={styles.formGroup}>
        <label style={styles.label}>Job Number *</label>
        <input
          type="text"
          name="job_number"
          value={formData.job_number}
          onChange={handleInputChange}
          style={styles.input}
          placeholder="Enter job number (e.g., 11-2024-001)"
        />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Job Name *</label>
        <input
          type="text"
          name="job_name"
          value={formData.job_name}
          onChange={handleInputChange}
          style={styles.input}
          placeholder="Enter project name"
        />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Client</label>
        <input
          type="text"
          name="client_name"
          value={formData.client_name}
          onChange={handleInputChange}
          style={styles.input}
          placeholder="Enter client name (optional)"
        />
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Project type *</label>
        <select
          name="project_type"
          value={formData.project_type}
          onChange={handleInputChange}
          style={styles.select || styles.input}
        >
          <option value="">-- Select project type --</option>
          {projectTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <h4 style={styles.stepTitle}>Division & Disciplines</h4>
      <div style={styles.formGroup}>
        <label style={styles.label}>Division</label>
        <select
          name="division_id"
          value={formData.division_id}
          onChange={handleInputChange}
          style={styles.select}
        >
          <option value="">-- Auto (from estimate number / job prefix) --</option>
          {divisions.map(div => (
            <option key={div.id} value={div.id}>
              {div.division_number} - {div.division_name}
            </option>
          ))}
        </select>
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
          Leave on Auto and the portal sets the company from the estimate number
          (26-LG-… Canada, 26-US-… USA North, 26-FL-… USA South, 26-LI-… Tanks, 26-QC-… Quebec)
          or the job-number prefix (71/75 Canada, 61/63 LGL Inc., 30 USA North, 40 Tanks).
          The company drives letterhead and the currency of Maestro / Forecasting reports.
        </div>
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>
          Disciplines
          {formData.discipline_ids.length > 0 && (
            <span style={styles.selectedCount}>{formData.discipline_ids.length} selected</span>
          )}
        </label>
        <div style={styles.checkboxGrid}>
          {disciplines.map(disc => (
            <div
              key={disc.id}
              style={styles.checkboxItem(formData.discipline_ids.includes(disc.id))}
              onClick={() => handleDisciplineToggle(disc.id)}
            >
              <input
                type="checkbox"
                checked={formData.discipline_ids.includes(disc.id)}
                onChange={() => {}}
                style={styles.checkbox}
              />
              <span style={{ color: '#e0e0e0' }}>{disc.discipline_name}</span>
            </div>
          ))}
        </div>
        {disciplines.length === 0 && (
          <p style={{ color: '#888', fontStyle: 'italic' }}>No disciplines available</p>
        )}
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <h4 style={styles.stepTitle}>
        User Assignments
        {formData.user_assignments.length > 0 && (
          <span style={styles.selectedCount}>{formData.user_assignments.length} selected</span>
        )}
      </h4>
      <input
        type="text"
        placeholder="Search users by name or email..."
        value={userSearchTerm}
        onChange={(e) => setUserSearchTerm(e.target.value)}
        style={styles.searchInput}
      />
      <div style={styles.userList}>
        {filteredUsers.length === 0 ? (
          <p style={{ color: '#888', padding: '1rem', textAlign: 'center' }}>
            {userSearchTerm ? 'No users found matching your search' : 'No users available'}
          </p>
        ) : (
          filteredUsers.map(user => (
            <div
              key={user.id}
              style={styles.userItem(isUserSelected(user.id))}
            >
              <div style={styles.userInfo} onClick={() => handleUserToggle(user.id)}>
                <input
                  type="checkbox"
                  checked={isUserSelected(user.id)}
                  onChange={() => {}}
                  style={styles.checkbox}
                />
                <div style={{ minWidth: 0 }}>
                  <span style={styles.userName}>{user.display_name}</span>
                  <span style={styles.userEmail}>{user.email}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>Create New Project</h3>
          <button onClick={handleClose} style={styles.closeButton}>×</button>
        </div>

        {renderStepIndicator()}

        <div style={styles.content}>
          {loadingData ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              Loading...
            </div>
          ) : (
            <>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </>
          )}

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            onClick={currentStep === 1 ? handleClose : handleBack}
            disabled={loading}
            style={{
              ...styles.button('secondary'),
              ...(loading ? styles.buttonDisabled : {})
            }}
          >
            {currentStep === 1 ? 'Cancel' : '← Back'}
          </button>
          
          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceedToNextStep() || loading}
              style={{
                ...styles.button('primary'),
                ...(!canProceedToNextStep() || loading ? styles.buttonDisabled : {})
              }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canProceedToNextStep()}
              style={{
                ...styles.button('success'),
                ...(loading || !canProceedToNextStep() ? styles.buttonDisabled : {})
              }}
            >
              {loading ? 'Creating...' : '✓ Create Project'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
